import Link from 'next/link';
import { Trans, useTranslation } from 'next-i18next';
import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';

import { V1A_IMG, V1A_NAMESPACE } from '../constants';
import { useReducedMotion } from '../hooks/use-reduced-motion';

interface Cover {
  file: string;
  name: string;
  width: number;
  height: number;
  /** transparent art: WebP only (the AV1 encoder used for the design writes no alpha) */
  top?: boolean;
}

const ROW_A: Cover[] = [
  { file: 'cover-01-256', name: 'Valorant', width: 256, height: 341 },
  { file: 'cover-02-256', name: 'CS2', width: 256, height: 341 },
  { file: 'cover-03-256', name: 'League of Legends', width: 256, height: 341 },
  { file: 'cover-04-256', name: 'Fortnite', width: 256, height: 341 },
  { file: 'cover-05-256', name: 'Dota 2', width: 256, height: 341 },
  { file: 'cover-06-256', name: 'Apex Legends', width: 256, height: 341 },
  { file: 'cover-07-248', name: 'Rainbow Six Siege', width: 248, height: 391, top: true },
  { file: 'cover-08-256', name: 'Overwatch 2', width: 256, height: 341 },
  { file: 'cover-09-256', name: 'PUBG', width: 256, height: 341 },
];

const ROW_B: Cover[] = [
  { file: 'cover-10-256', name: 'World of Warcraft', width: 256, height: 341 },
  { file: 'cover-11-256', name: 'Escape From Tarkov', width: 256, height: 341 },
  { file: 'cover-12-256', name: 'Final Fantasy XIV', width: 256, height: 341 },
  { file: 'cover-13-250', name: 'Albion Online', width: 250, height: 392, top: true },
  { file: 'cover-14-256', name: 'Ragnarok', width: 256, height: 341 },
  { file: 'cover-15-256', name: 'Lineage II', width: 256, height: 341 },
  { file: 'cover-16-248', name: 'Tibia', width: 248, height: 391, top: true },
  { file: 'cover-17-256', name: 'StarCraft', width: 256, height: 341 },
  { file: 'cover-18-248', name: 'Warface', width: 248, height: 391, top: true },
];

const ROWS = [
  { id: 'a', covers: ROW_A, labelKey: 'games.row_a' },
  { id: 'b', covers: ROW_B, labelKey: 'games.row_b' },
] as const;

interface Loop {
  reps: number;
  gap: number;
  duration: number;
}

function CoverTile({ cover, hidden }: { cover: Cover; hidden: boolean }) {
  const image = <img src={`${V1A_IMG}/${cover.file}.webp`} width={cover.width} height={cover.height} alt={hidden ? '' : cover.name} loading='lazy' decoding='async' />;
  return (
    <figure className={`v1-cover ${cover.top ? 'v1-cover--top' : ''}relative !m-0 flex w-[clamp(6.5rem,5rem_+_6vw,10.5rem)] flex-none flex-col overflow-hidden [&_img]:block [&_img]:size-full [&_img]:object-cover`} aria-hidden={hidden || undefined}>
      <span className='v1-cover__art relative block aspect-[3/4] overflow-hidden'>
        {cover.top ? (
          image
        ) : (
          <picture>
            <source type='image/avif' srcSet={`${V1A_IMG}/${cover.file}.avif`} />
            {image}
          </picture>
        )}
      </span>
      <figcaption className='v1-cover__cap flex min-h-11 items-center gap-1.5 px-2 pb-2 pt-[.4375rem] text-[length:var(--t-micro)] font-semibold leading-[1.2] tracking-[.005em] text-[#FFFFFF]'>
        <span className='v1-cover__tick size-[5px] flex-none' aria-hidden='true' />
        {cover.name}
      </figcaption>
    </figure>
  );
}

/**
 * The two cover rows loop seamlessly: once measured, each track is widened with hidden copies until one half
 * outruns the screen, then doubled so the CSS keyframes can slide exactly one half. Reduced motion keeps the
 * authored list as a plain, keyboard-reachable horizontal scroller.
 */
export function Games() {
  const { t } = useTranslation(V1A_NAMESPACE);
  const reducedMotion = useReducedMotion();
  const marqueeRef = useRef<HTMLDivElement>(null);
  const trackRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [loops, setLoops] = useState<Loop[] | null>(null);
  const [idle, setIdle] = useState(false);
  const [paused, setPaused] = useState(false);

  useEffect(() => setPaused(reducedMotion), [reducedMotion]);

  useEffect(() => {
    if (reducedMotion) {
      setLoops(null);
      return;
    }
    const screenSize = window.screen || ({} as Partial<Screen>);
    const viewport = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0, screenSize.width || 0, screenSize.height || 0);
    const need = Math.max(1400, viewport + 640);
    setLoops(
      ROWS.map((_row, index) => {
        const track = trackRefs.current[index];
        if (!track) return { reps: 1, gap: 0, duration: 46 };
        const style = window.getComputedStyle(track);
        const gap = parseFloat(style.columnGap || style.gap) || 0;
        const half = track.scrollWidth + gap;
        const reps = half > gap ? Math.min(6, Math.max(1, Math.ceil(need / half))) : 1;
        return { reps, gap, duration: parseFloat(style.animationDuration) || 46 };
      })
    );
  }, [reducedMotion]);

  useEffect(() => {
    const marquee = marqueeRef.current;
    if (!loops || !marquee || typeof IntersectionObserver === 'undefined') return undefined;
    setIdle(true);
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => setIdle(!entry.isIntersecting)), { rootMargin: '200px 0px', threshold: 0 });
    observer.observe(marquee);
    return () => observer.disconnect();
  }, [loops]);

  const marqueeClass = ['v1-marquee', idle && 'is-idle', paused && 'is-paused'].filter(Boolean).join(' ');

  return (
    <section className='v1-games relative isolate overflow-x-clip border-t border-[rgb(255_255_255/.08)] bg-[linear-gradient(180deg,#0E1116,#0B0D10)] py-[clamp(3rem,1.5rem_+_5vw,6rem)]' aria-labelledby='v1-games-title'>
      <div className='v1-games__bg' aria-hidden='true' />
      <div className='mx-auto w-full max-w-[90rem] px-[var(--gutter)]'>
        <div className='v1-head v1-head--center mx-auto max-w-[46rem] text-center [&_.v1-lead]:mx-auto'>
          <p className='v1-eyebrow v1-eyebrow--center m-0 inline-flex items-center justify-center gap-2 text-[length:var(--t-micro)] font-semibold uppercase leading-[1.3] tracking-[.16em] text-[#9AA3B5]' data-reveal>
            <span className='v1-eyebrow__dot size-1.5 flex-none rounded-full bg-[#93FF18] shadow-[0_0_10px_rgb(147_255_24/.8)]' aria-hidden='true' />
            {t('games.eyebrow')}
          </p>
          <h2 className='v1-h2 mt-3 text-balance text-[length:var(--t-h2)] font-bold leading-[1.04] tracking-[-.032em] text-[#FFFFFF] min-[1920px]:text-[length:3.5rem]' id='v1-games-title' data-reveal>
            <Trans t={t} i18nKey='games.title' components={{ hl: <span className='v1-hl !text-[#93FF18] [text-shadow:0_0_22px_rgb(147_255_24/.3)] forced-colors:!text-[LinkText]' /> }} />
          </h2>
          <p className='v1-lead mt-4 max-w-[44rem] text-pretty text-[length:var(--t-lead)] leading-[1.55] text-[#CBD5E1]' data-reveal>
            {t('games.lead')}
          </p>
        </div>
      </div>

      {/* The loop's geometry (keyframes, folded gaps) is physical: the covers scroll the same way in every locale. */}
      <div ref={marqueeRef} className={marqueeClass} data-reveal data-marquee dir='ltr'>
        {ROWS.map((row, rowIndex) => {
          const loop = loops?.[rowIndex];
          const trackStyle = loop ? ({ '--mq-dur': `${(loop.duration * loop.reps).toFixed(2)}s`, ...(loop.gap ? { '--mq-gap': `${loop.gap}px` } : {}) } as CSSProperties) : undefined;
          const trackClass = ['v1-marquee__track', loop && loop.gap && 'is-folded', loop && 'is-built'].filter(Boolean).join(' ');
          const widened = loop ? Array.from({ length: loop.reps }, (_value, copy) => row.covers.map((cover) => ({ cover, key: `${copy}-${cover.file}`, hidden: copy > 0 }))).flat() : row.covers.map((cover) => ({ cover, key: `0-${cover.file}`, hidden: false }));
          const tiles = loop ? [...widened, ...widened.map((tile) => ({ ...tile, key: `d-${tile.key}`, hidden: true }))] : widened;
          return (
            <div key={row.id} className='v1-marquee__row' data-marquee-row={row.id} tabIndex={loop ? undefined : 0} role='group' aria-label={t(row.labelKey)}>
              <div
                ref={(element) => {
                  trackRefs.current[rowIndex] = element;
                }}
                className={trackClass}
                style={trackStyle}
              >
                {tiles.map((tile) => (
                  <CoverTile key={tile.key} cover={tile.cover} hidden={tile.hidden} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div className='mx-auto w-full max-w-[90rem] px-[var(--gutter)]'>
        <div className='v1-mo-bar v1-mo-bar--marquee -mb-2 mt-[clamp(.875rem,.5rem_+_1vw,1.25rem)] flex justify-end motion-reduce:hidden'>
          <button className='v1-mo' type='button' data-motion='marquee' aria-pressed={paused} aria-label={paused ? t('games.motion.resume_aria') : t('games.motion.pause_aria')} title={t('games.motion.title')} onClick={() => setPaused((previous) => !previous)}>
            <svg className='v1-mo__ic v1-mo__ic--pause' viewBox='0 0 24 24' width='14' height='14' aria-hidden='true' focusable='false'>
              <path d='M8.5 5.5v13M15.5 5.5v13' />
            </svg>
            <svg className='v1-mo__ic v1-mo__ic--play' viewBox='0 0 24 24' width='14' height='14' aria-hidden='true' focusable='false'>
              <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
            </svg>
            <span data-motion-label>{paused ? t('games.motion.resume') : t('games.motion.pause')}</span>
          </button>
        </div>

        <p className='v1-games__foot mt-[clamp(1.5rem,1rem_+_1.5vw,2rem)] flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center text-[length:var(--t-body)] text-[#9AA3B5]' data-reveal>
          <span className='v1-games__more'>{t('games.more')}</span>{' '}
          <Link className='v1-games__link inline-flex min-h-11 items-center gap-1.5 py-2 font-semibold !text-[#93FF18] [&_svg]:flex-none' href='/games' target='_blank' rel='noopener'>
            {t('games.link')}
            <span className='sr-only'>{t('games.new_tab')}</span>
            <svg viewBox='0 0 24 24' width='16' height='16' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false' className='rtl:-scale-x-100'>
              <path d='M5 12h14' />
              <path d='m13 6 6 6-6 6' />
            </svg>
          </Link>
        </p>
        <p className='v1-games__note mt-1 text-center text-[length:var(--t-micro)] leading-normal text-[#8C95A8]' data-reveal>
          {t('games.note')}
        </p>
      </div>
    </section>
  );
}
